// Dashboard Onboarding section

import { useNavigate } from "react-router-dom"
import {
    FaPlus,
    FaUserCheck,
    FaUserClock,
    FaUsers,
} from "react-icons/fa"
import { Button } from "@/modules/shared/ui/components/button"
import { DashboardStatsDto } from "@/modules/dashboard/infrastructure/dashboard-service"
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Cell } from "recharts";
import { EMPLOYEE_STATUS_COLOURS } from "./COLOR_GROUPS";
import { SectionHeader } from "./SectionHeader";
import { StatCard } from "./StatCard";

export const OnboardingSection = ({ stats, loading }: { stats: DashboardStatsDto | undefined; loading: boolean }) => {
    const o = stats?.onboarding
    const navigate = useNavigate()

    return (
        <div className="space-y-4">
            <SectionHeader title="Onboarding" description="Employee headcount and onboarding progress" />

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard
                    icon={FaUsers}
                    label="Total Employees"
                    value={o?.totalEmployees ?? 0}
                    sub="active + pending"
                    colour="bg-cyan-500"
                    loading={loading}
                    onClick={() => navigate('/onboarding/employees')}
                />
                <StatCard
                    icon={FaUserCheck}
                    label="Active"
                    value={o?.activeEmployees ?? 0}
                    sub="fully onboarded"
                    colour="bg-emerald-500"
                    loading={loading}
                    onClick={() => navigate('/onboarding/employees')}
                />
                <StatCard
                    icon={FaUserClock}
                    label="In Progress"
                    value={o?.pendingEmployees ?? 0}
                    sub="onboarding pending"
                    colour="bg-amber-500"
                    loading={loading}
                    onClick={() => navigate('/onboarding/employees')}
                />
            </div>

            {/* Employee status chart */}
            <div className="bg-card rounded-xl border border-border shadow-sm p-5">
                <h4 className="text-sm font-semibold text-foreground mb-4">Employee Status Breakdown</h4>
                {loading ? (
                    <div className="h-48 flex items-center justify-center">
                        <div className="text-sm text-muted-foreground animate-pulse">Loading chart…</div>
                    </div>
                ) : o && o.employeesByStatus.some((s) => s.count > 0) ? (
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart
                            data={o.employeesByStatus}
                            margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
                            barSize={48}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                            <XAxis
                                dataKey="status"
                                tick={{ fontSize: 12, fill: "var(--muted-foreground)" }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                allowDecimals={false}
                                tick={{ fontSize: 12, fill: "var(--muted-foreground)" }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip
                                cursor={{ fill: "var(--muted)" }}
                                contentStyle={{ borderRadius: "8px", border: "1px solid var(--border)", fontSize: "12px", backgroundColor: "var(--popover)", color: "var(--popover-foreground)" }}
                                formatter={(value: number) => [value, "Employees"]}
                            />
                            <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                                {o.employeesByStatus.map((entry) => (
                                    <Cell key={entry.status} fill={EMPLOYEE_STATUS_COLOURS[entry.status] ?? "#6366f1"} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-48 flex flex-col items-center justify-center text-center">
                        <FaUsers className="h-8 w-8 text-muted-foreground/30 mb-2" />
                        <p className="text-sm text-muted-foreground mb-3">No employees yet</p>
                        <Button
                            size="sm"
                            onClick={() => navigate("/onboarding/employees")}
                            className="text-primary-foreground"
                        >
                            <FaPlus className="mr-2 h-3 w-3" />
                            Add Employee
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}